import {StoreAction} from "orpyx-web-common";
import produce from "immer";
import {
    PatientInformationDailyInsightsIndexActions,
    PatientInformationDailyInsightsCachingActions,
    PatientInformationDailyInsightsNavigationActions
} from "./patient-information-daily-insights.actions";

import { PATIENT_INSIGHTS_BATCH_SIZE } from "appConstants";
import { CachedInsightImage, InsightImageStatus, PatientInformationDailyInsightsState } from "../patient-information-daily-insights.interface";
import { combineReducers } from "redux";

const getInitialState = (): PatientInformationDailyInsightsState => {
    const initialState = {
        initialized: false,
        loadedImages: [],
        currentImage: undefined,
        loading: false,
        refreshing: false,
        error: undefined,
        currentImageIndex: 0,
        hasPrevious: true,
        hasNext: false,
        additionalInsightsAvailable: true,
    }
    return {...initialState};
}

const mergeArrays = (loadedImages: CachedInsightImage[], fetchedImages: CachedInsightImage[]): CachedInsightImage[] => {
    const combinedImages = [...loadedImages, ...(fetchedImages || [])];

    if (combinedImages.length === 0) {
        return [];
    }

    // Dedupe loaded images so we are guaranteed one image per date in the array; newest duplicate wins.
    const uniqueMap = new Map<string, CachedInsightImage>();
    for (const image of combinedImages) {
        uniqueMap.set(image.date, image);
    }
    
    let uniqueArray = Array.from(uniqueMap.values());

    uniqueArray.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        return dateB.getTime() - dateA.getTime();
    });

    return uniqueArray;
}

const PatientInformationDailyInsightsIndexReducers = (state: PatientInformationDailyInsightsState, action: StoreAction<PatientInformationDailyInsightsIndexActions>): PatientInformationDailyInsightsState => {
    return produce(state, (draft: PatientInformationDailyInsightsState) => {
        switch (action.type) {

            case PatientInformationDailyInsightsIndexActions.RESET_PATIENT_INFORMATION_DAILY_INSIGHTS:
                const originalState = getInitialState();
                
                Object.assign(draft, originalState);
                break;

            case PatientInformationDailyInsightsIndexActions.GET_PATIENT_INFORMATION_DAILY_INSIGHTS:
                draft.loading = true;
                if (!state.initialized) {
                    draft.initialized = false;
                }
                draft.error = undefined;
                draft.hasPrevious = false;
                break;


            case PatientInformationDailyInsightsIndexActions.GET_PATIENT_INFORMATION_DAILY_INSIGHTS_SUCCESSFULLY:

                if (action.payload && action.payload.length > 0) {
                    draft.loadedImages = mergeArrays(state.loadedImages, action.payload);
                }

                draft.error = false;
                draft.loading = false;
                draft.refreshing = false;
                draft.initialized = true;

                const additionalInsightsAvailable = action.payload.length === PATIENT_INSIGHTS_BATCH_SIZE;
                draft.additionalInsightsAvailable = additionalInsightsAvailable;
                draft.hasPrevious = state.currentImageIndex < Object.keys(draft.loadedImages).length - 1 || additionalInsightsAvailable;
                draft.hasNext = state.currentImageIndex > 0;
                draft.currentImage = draft.loadedImages[state.currentImageIndex];
                break;
            case PatientInformationDailyInsightsIndexActions.GET_PATIENT_INFORMATION_DAILY_INSIGHTS_FAILED:
                draft.error = action.payload;
                draft.refreshing = false;
                draft.loading = false;
                draft.hasPrevious = draft.currentImageIndex < state.loadedImages.length - 1  || state.additionalInsightsAvailable;
                break;

        }
    });
};


const PatientInformationDailyInsightsCachingReducers = (state: PatientInformationDailyInsightsState, action: StoreAction<PatientInformationDailyInsightsCachingActions>): PatientInformationDailyInsightsState => {
    return produce(state, (draft: PatientInformationDailyInsightsState) => {
        switch (action.type) {
            case PatientInformationDailyInsightsCachingActions.FETCH_SINGLE_DAILY_INSIGHTS_IMAGE:
                break;

            case PatientInformationDailyInsightsCachingActions.FETCH_SINGLE_DAILY_INSIGHTS_IMAGE_FAILED:
                const failedDate = action.payload.date 
                const failedImageIndex = draft.loadedImages.findIndex(image => image.date === failedDate);
                if (failedImageIndex !== -1) {
                    draft.loadedImages[failedImageIndex].status = InsightImageStatus.ERROR
               }
               break;

            case PatientInformationDailyInsightsCachingActions.FETCH_SINGLE_DAILY_INSIGHTS_IMAGE_SUCCESSFULLY:
                const date = action.payload.date;
                const imageDataUrl = action.payload.blobUrl;

                const imageIndex = draft.loadedImages.findIndex(image => image.date === date);
                if (imageIndex !== -1) {
                    draft.loadedImages[imageIndex] = {
                        ...draft.loadedImages[imageIndex],
                        blobUrl: imageDataUrl,
                        status: InsightImageStatus.READY,
                    }

                    if (draft.currentImage && draft.currentImage.date === draft.loadedImages[imageIndex].date) {
                        draft.currentImage = draft.loadedImages[imageIndex];
                    }
                }
                break;
            case PatientInformationDailyInsightsCachingActions.RELOAD_CURRENT_INSIGHT_IMAGE:
                draft.refreshing = true;
                break;


        }
    });
};


const PatientInformationDailyInsightsNavigationReducers = (state: PatientInformationDailyInsightsState, action: StoreAction<PatientInformationDailyInsightsNavigationActions>): PatientInformationDailyInsightsState => {
    return produce(state, (draft: PatientInformationDailyInsightsState) => {
        switch (action.type) {
            case PatientInformationDailyInsightsNavigationActions.NAVIGATE_TO_DAILY_INSIGHTS_PREVIOUS:
                if ( state.currentImageIndex < state.loadedImages.length - 1) {
                    draft.currentImageIndex++;
                }
                draft.currentImage = state.loadedImages[draft.currentImageIndex];
                draft.hasPrevious = draft.currentImageIndex < state.loadedImages.length - 1  || (state.additionalInsightsAvailable && !state.loading);
                draft.hasNext = draft.currentImageIndex > 0;
                break;

            case PatientInformationDailyInsightsNavigationActions.NAVIGATE_TO_DAILY_INSIGHTS_NEXT:
                if ( state.currentImageIndex > 0) { draft.currentImageIndex--; }
                draft.currentImage = state.loadedImages[draft.currentImageIndex];
                draft.hasNext = draft.currentImageIndex !== 0;
                draft.hasPrevious = draft.currentImageIndex < state.loadedImages.length - 1  || state.additionalInsightsAvailable;
                break;

            case PatientInformationDailyInsightsNavigationActions.NAVIGATE_TO_DAILY_INSIGHTS_LATEST:
                draft.currentImageIndex = 0;
                draft.currentImage = state.loadedImages[0];
                draft.hasPrevious = draft.currentImageIndex < state.loadedImages.length - 1  || state.additionalInsightsAvailable;
                draft.hasNext = false;
                break;
        }
    });
};

const PatientInformationDailyInsightsReducers = (state = getInitialState(), action: StoreAction<PatientInformationDailyInsightsNavigationActions | PatientInformationDailyInsightsCachingActions | PatientInformationDailyInsightsIndexActions>): PatientInformationDailyInsightsState => {
    let newState = state;

    if (Object.values(PatientInformationDailyInsightsIndexActions).includes(action.type as PatientInformationDailyInsightsIndexActions)) {
        newState = PatientInformationDailyInsightsIndexReducers(newState, action as StoreAction<PatientInformationDailyInsightsIndexActions>);
    }
    else if (Object.values(PatientInformationDailyInsightsCachingActions).includes(action.type as PatientInformationDailyInsightsCachingActions)) {
        newState = PatientInformationDailyInsightsCachingReducers(newState, action as StoreAction<PatientInformationDailyInsightsCachingActions>);
    }
    else if (Object.values(PatientInformationDailyInsightsNavigationActions).includes(action.type as PatientInformationDailyInsightsNavigationActions)) {
        newState = PatientInformationDailyInsightsNavigationReducers(newState, action as StoreAction<PatientInformationDailyInsightsNavigationActions>);
    };
   
  return newState;
};


export default PatientInformationDailyInsightsReducers;